import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import * as PopupConstants from '../../../shared/constants/popup-constants';
import { UserService } from '../../../services/user-management/user.service';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { InviteUserPayload } from '../../../interfaces/invite-user';
import { InputComponent } from '../../../shared/input/input.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { Subscription } from 'rxjs';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';
import { HttpErrorResponse } from '@angular/common/http';

type DropdownOption = { value: any; label: string };

@Component({
  selector: 'app-invite-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    InputComponent,
    ButtonsComponent,
  ],
  templateUrl: './invite-user-dialog.component.html',
  styleUrl: './invite-user-dialog.component.css',
})
export class InviteUserDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private messageModalService = inject(MessageModalService);
  public dialogRef = inject(MatDialogRef<InviteUserDialogComponent>);
  subscription$!: Subscription;

  inviteForm!: FormGroup;
  isSubmitting = false;
  errorMessage: string = '';

  name = new FormControl('', [Validators.required, Validators.maxLength(256), noWhitespaceValidator()]);
  email = new FormControl('', [Validators.required, Validators.email, noWhitespaceValidator()]);
  phone = new FormControl('', [Validators.required, Validators.pattern(/^[+()\-0-9]+$/), noWhitespaceValidator()]);

  textInputType = 'text';
  emailInputType = 'email';

  nameLabel = $localize`Full Name`;
  namePlaceholder = $localize`Enter full name`;

  emailLabel = $localize`Email Address`;
  emailPlaceholder = $localize`Enter email address`;

  phoneLabel = $localize`Phone Number`;
  phonePlaceholder = $localize`Enter phone number`;

  arialLabel = PopupConstants.ARIAL_LABEL;
  btnMat = PopupConstants.BTN_MAT;
  btnStyleText = PopupConstants.BTN_STYLE_TEXT;

  btnMatFlat = PopupConstants.BTN_MAT_FLAT;
  btnStyleFilledSmall = PopupConstants.BTN_STYLE_FILLED_SMALL;
  btnStyleFilledSmallDisabled = PopupConstants.BTN_STYLE_FILLED_SMALL_DISABLED;

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      name: this.name,
      email: this.email,
      phone: this.phone,
    });
  }
  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const formValue = this.inviteForm.value;

    const payload: InviteUserPayload = {
      name: formValue.name,
      email: formValue.email,
      phone: formValue.phone,
    };

    this.subscription$ = this.userService.inviteUser(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageModalService.openModal($localize`Success`, $localize`Invitation sent successfully!`);
          this.dialogRef.close(true);
        } else {
          this.messageModalService.openModal('Invite Failed', response.message || 'An unknown error occurred.');
          this.dialogRef.close(false);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        let title = 'Invite Failed';
        let message = err.error?.message || 'User already a member';

        this.messageModalService.openModal(title, message);
        this.dialogRef.close(false);
      },
    });
  }

  getFormControl(controlName: string): FormControl {
    return this.inviteForm.get(controlName) as FormControl;
  }
  get nameControl() {
    return this.inviteForm.get('name');
  }
  get emailControl() {
    return this.inviteForm.get('email');
  }
  get phoneControl() {
    return this.inviteForm.get('phone');
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
